// [목적] /explore 검색어·필터 모달(기간·테마)·정렬 상태를 URL 쿼리와 동기화하는 공용 훅
// [이유] M7 P1(발견 화면 서버 검색·필터) — ExplorePage(데스크톱)·ExploreMobile 이 동일 URL 쿼리(q/tags/
//        duration/sort)를 공유해야 필터 모달을 한 벌만 두고 두 레이아웃에서 재사용할 수 있다.
//        packages/types listFoliosQuerySchema(tags 복수·minDurationDays·maxDurationDays)에 맞춰
//        duration 라벨(전체/당일/1박 2일/2박 3일+)을 min/max 일수 범위로 변환한다.
// [사이드 이팩트] useSearchParams 로 히스토리 엔트리를 replace(뒤로가기 스팸 방지) — 필터 변경은 새 히스토리
//               스택을 쌓지 않는다. 빈 값(all/전체 등)은 쿼리에서 제거되어 URL 이 깔끔하게 유지된다.
// [수정 시 고려사항] DURATION_RANGES 라벨 변경 시 FilterModal.tsx 의 DURATION_OPTIONS 와 동기 필수.
//                   [2026-09-17 메인 검수] 카테고리 칩 10종도 같은 URL 상태로 연결 — 칩은 tags 1개 또는
//                   duration='당일' 로 매핑(CHIP_FILTERS), 활성 칩은 URL 에서 파생(별도 state 없음 → 모달과 충돌 없음).
//                   모달에서 태그 2개 이상 등 칩으로 표현 불가한 조합이면 활성 칩 없음('').
//                   칩 id 추가 시 ExplorePage/ExploreMobile FILTERS 와 CHIP_FILTERS 동기 필수.

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FolioFeedSort } from './useFolioFeed.js';

export type DurationRangeLabel = '전체' | '당일' | '1박 2일' | '2박 3일+';

/** [목적] duration 라벨 → BE minDurationDays/maxDurationDays 매핑. undefined 항목은 조건절 생략(제한 없음). */
export const DURATION_RANGES: Record<DurationRangeLabel, { min?: number; max?: number }> = {
  전체: {},
  당일: { min: 1, max: 1 },
  '1박 2일': { min: 2, max: 2 },
  '2박 3일+': { min: 3 },
};

/** [목적] 카테고리 칩 id → 서버 필터(tags 1개 또는 기간) 매핑. 'all' 은 둘 다 해제. */
export const CHIP_FILTERS: Record<string, { tag?: string; duration?: DurationRangeLabel }> = {
  pet: { tag: '반려견' },
  cafe: { tag: '카페' },
  camp: { tag: '캠핑' },
  sea: { tag: '바다' },
  mountain: { tag: '산' },
  food: { tag: '맛집' },
  short: { duration: '당일' },
  surf: { tag: '서핑' },
  access: { tag: '무장애' },
};

/** [목적] 현재 tags·duration 으로 활성 칩 id 파생 — 칩으로 표현 불가한 조합이면 '' */
export function activeChipId(tags: string[], duration: DurationRangeLabel): string {
  if (tags.length === 0 && duration === '전체') return 'all';
  const hit = Object.entries(CHIP_FILTERS).find(([, f]) =>
    f.tag
      ? tags.length === 1 && tags[0] === f.tag && duration === '전체'
      : tags.length === 0 && duration === f.duration,
  );
  return hit ? hit[0] : '';
}

export interface UseExploreFiltersReturn {
  q: string;
  tags: string[];
  duration: DurationRangeLabel;
  sort: FolioFeedSort;
  minDurationDays: number | undefined;
  maxDurationDays: number | undefined;
  setQ: (q: string) => void;
  setTags: (tags: string[]) => void;
  setDuration: (duration: DurationRangeLabel) => void;
  setSort: (sort: FolioFeedSort) => void;
  reset: () => void;
  /** 활성 카테고리 칩 id('all' | CHIP_FILTERS 키 | '') */
  chip: string;
  setChip: (id: string) => void;
}

/**
 * [목적] /explore 쿼리(q/tags/duration/sort) 를 URL 과 동기화하는 훅
 * [이유] ExplorePage(Desktop)/ExploreMobile 이 동일 URLSearchParams 를 공유해 필터 모달 상태가 뷰포트
 *        전환(리사이즈) 후에도 유지되도록 한다.
 * [사이드 이팩트] setParams(..., { replace: true }) — 필터 변경마다 히스토리 스택 미생성.
 * [수정 시 고려사항] tags 는 콤마 구분 문자열로 URL 에 저장(예: ?tags=바다,카페) — listFoliosQuerySchema
 *                   tagsQueryPreprocess 가 콤마 문자열을 배열로 파싱하므로 buildQuery(folioClient.ts)의
 *                   String(array) 직렬화(콤마 join)와 왕복 호환된다.
 */
export function useExploreFilters(): UseExploreFiltersReturn {
  const [params, setParams] = useSearchParams();

  const q = params.get('q') ?? '';
  const tags = useMemo(() => {
    const raw = params.get('tags');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [params]);
  const duration = (params.get('duration') as DurationRangeLabel | null) ?? '전체';
  const sort = (params.get('sort') as FolioFeedSort | null) ?? 'latest';
  const range = DURATION_RANGES[duration] ?? {};

  const update = useCallback(
    (patch: Partial<{ q: string; tags: string[]; duration: string; sort: string }>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            const isEmpty =
              value === undefined ||
              value === '' ||
              value === '전체' ||
              (Array.isArray(value) && value.length === 0);
            if (isEmpty) next.delete(key);
            else next.set(key, Array.isArray(value) ? value.join(',') : String(value));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return {
    q,
    tags,
    duration,
    sort,
    minDurationDays: range.min,
    maxDurationDays: range.max,
    setQ: (v) => update({ q: v }),
    setTags: (v) => update({ tags: v }),
    setDuration: (v) => update({ duration: v }),
    setSort: (v) => update({ sort: v }),
    reset: () => update({ q: '', tags: [], duration: '전체' }),
    chip: activeChipId(tags, duration),
    setChip: (id) => {
      const f = CHIP_FILTERS[id] ?? {};
      update({ tags: f.tag ? [f.tag] : [], duration: f.duration ?? '전체' });
    },
  };
}
